import { useQuery } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Fetches the permission bundle for a single entity (canRead, canUpdate,
 * canDelete, canInsertChild). Called once per entity detail page and cached
 * by entityId.
 *
 * Only active when both the `trpc` and `permission_view_v2` feature flags
 * are enabled.
 */
export const useEntityPermissions = (entityId: string | undefined) => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const isPermViewV2 = useIsFeatureFlagEnabled('permission_view_v2');
  const isEnabled = trpcEnabled && isPermViewV2;
  const trpc = useTRPC();

  const { data, isLoading } = useQuery({
    ...trpc.frontend.permission.getEntityPermissions.queryOptions({
      entityId: entityId!,
    }),
    enabled: isEnabled && !!entityId,
    gcTime: 30 * 60 * 1000,
    staleTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    refetchInterval: false,
  });

  return {
    canRead: data?.canRead ?? false,
    canUpdate: data?.canUpdate ?? false,
    canDelete: data?.canDelete ?? false,
    canInsertChild: data?.canInsertChild ?? false,
    isLoading: isEnabled ? isLoading : true,
  };
};
